import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const checkIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const [auth, setAuth] = useState(() => {
    const token = Cookies.get("token");
    const role = Cookies.get("role");
    const email = Cookies.get("email");
    const userId = Cookies.get("userId");
    const expireTime = Cookies.get("expireTime");

    if (token && role) {
      return {
        token,
        email: email || "",
        role,
        userId: userId || "",
        expireTime: expireTime || "",
      };
    }
    return null;
  });

  // Check token expiration function
  const checkTokenExpiration = useCallback(() => {
    if (!auth?.expireTime) return false;

    const now = Math.floor(Date.now() / 1000);
    const expire = parseInt(auth.expireTime, 10);

    if (expire <= now) {
      logout();
      toast.success("Session Expired - Auto Logout!");
      return true;
    }
    return false;
  }, [auth?.expireTime]);

  // Logout function
  const logout = useCallback(() => {
    const role = auth?.role;
    const cookieRemoveOptions = { path: "/" };

    // Clear the interval when logging out
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    Cookies.remove("token", cookieRemoveOptions);
    Cookies.remove("email", cookieRemoveOptions);
    Cookies.remove("role", cookieRemoveOptions);
    Cookies.remove("userId", cookieRemoveOptions);
    Cookies.remove("expireTime", cookieRemoveOptions);

    setAuth(null);

    if (role === "admin" || role === "maintainer") {
      navigate(`/${role}/login`, { replace: true });
    } else if (role === "student") {
      navigate("/student-login", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  }, [auth?.role, navigate]);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Start continuous checking
  useEffect(() => {
    if (!auth?.expireTime) return;

    // Clear any existing interval
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    // Check every 1 second
    checkIntervalRef.current = setInterval(() => {
      checkTokenExpiration();
    }, 1000);

    // Add activity listeners
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "wheel",
      "touchmove",
    ];

    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [auth?.expireTime, checkTokenExpiration, updateActivity]);

  // Check on route changes
  useEffect(() => {
    checkTokenExpiration();
  }, [location.pathname, checkTokenExpiration]);

  // Check when page becomes visible
  useEffect(() => {
    if (!auth?.expireTime) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkTokenExpiration();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkTokenExpiration]);

  const login = ({ token, email, role, userId, expireTime }) => {
    const cookieOptions = {
      expires: 7,
      secure: true,
      sameSite: "Strict",
    };

    Cookies.set("token", token, cookieOptions);
    Cookies.set("email", email, cookieOptions);
    Cookies.set("role", role, cookieOptions);
    Cookies.set("userId", userId, cookieOptions);
    Cookies.set("expireTime", expireTime, cookieOptions);

    setAuth({ token, email, role, userId, expireTime });

    if (role === "admin" || role === "maintainer") {
      navigate("/admin/dashboard");
    } else {
      navigate("/member-profile");
    }
  };

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
